import { LandingHeader } from './LandingHeader'
import { LandingFooter } from './LandingFooter'
import { playgroundUrl } from './playgroundUrl'

function CodeBlock({ filename, children }: { filename: string; children: React.ReactNode }) {
    return (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
            <div
                className="flex items-center gap-3 px-4 py-2.5 bg-surface-elevated"
                style={{ borderBottom: '1px solid var(--border-subtle)' }}
            >
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'color-mix(in srgb, var(--danger) 50%, transparent)' }} />
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'color-mix(in srgb, var(--warning) 50%, transparent)' }} />
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'color-mix(in srgb, var(--success) 50%, transparent)' }} />
                </div>
                <span className="text-[11px] font-mono text-text-muted">{filename}</span>
            </div>
            <pre className="p-5 font-mono text-[11px] sm:text-[13px] leading-[1.6] sm:leading-[1.8] overflow-x-auto">
                {children}
            </pre>
        </div>
    )
}

function KeywordCard({ keyword, description }: { keyword: string; description: string }) {
    return (
        <div className="flex items-start gap-4 p-3 rounded-lg" style={{ border: '1px solid var(--border)' }}>
            <code className="font-mono text-accent text-sm shrink-0 w-16 pt-0.5">{keyword}</code>
            <p className="text-sm text-text-secondary leading-relaxed">{description}</p>
        </div>
    )
}

export function LanguagePage() {
    const tryItUrl = playgroundUrl

    return (
        <div className="min-h-screen" style={{ scrollBehavior: 'smooth' }}>
            <LandingHeader />
            <main>
                {/* Hero */}
                <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
                    <div
                        className="absolute inset-0"
                        style={{
                            background: `radial-gradient(ellipse 80% 60% at 50% 40%, color-mix(in srgb, var(--accent) 8%, transparent), transparent)`,
                        }}
                    />
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: 'radial-gradient(circle, color-mix(in srgb, var(--accent) 10%, transparent) 1px, transparent 1px)',
                            backgroundSize: '32px 32px',
                        }}
                    />
                    <div
                        className="absolute top-[15%] right-[15%] w-[400px] h-[400px] rounded-full blur-[120px]"
                        style={{ background: 'color-mix(in srgb, var(--accent) 12%, transparent)' }}
                    />
                    <div
                        className="absolute bottom-[20%] left-[10%] w-[350px] h-[350px] rounded-full blur-[100px]"
                        style={{ background: 'color-mix(in srgb, var(--accent-subtle) 10%, transparent)' }}
                    />
                    <div
                        className="absolute bottom-0 left-0 right-0 h-48"
                        style={{ background: 'linear-gradient(to top, var(--bg), transparent)' }}
                    />

                    <div className="relative z-10 max-w-4xl w-full mx-auto px-6 pt-24 pb-16 text-center">
                        <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-6">
                            Language Reference
                        </p>
                        <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6">
                            The EN Notation
                        </h1>
                        <p className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed">
                            A plain-text language for describing systems. Four keywords. No schemas.
                        </p>
                    </div>
                </section>

                {/* Basics — split: code left, keywords right */}
                <section className="py-20 px-6" style={{ borderTop: '1px solid color-mix(in srgb, var(--border) 40%, transparent)' }}>
                    <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-2 lg:gap-12 items-start">
                        <div className="lg:sticky lg:top-24">
                            <CodeBlock filename="basics.en">
                                <div className="text-accent font-medium">auth service</div>
                                <div><span className="text-text-secondary">do: </span>validate token</div>
                                <div><span className="text-text-secondary">needs: </span>jwt token, signing key</div>
                                <div><span className="text-text-secondary">yields: </span>auth context, user id</div>
                                <div className="text-text-muted"># optional</div>
                                <div><span className="text-text-secondary">at: </span>identity</div>
                            </CodeBlock>
                        </div>
                        <div className="mt-8 lg:mt-0">
                            <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-4">Basics</p>
                            <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
                                Four keywords. That's the whole language.
                            </h2>
                            <p className="text-text-secondary mb-8 leading-relaxed">
                                Every EN statement starts with a subject line &mdash; the name of an entity.
                                Below it, use keywords to describe what it does, what it needs, and what it produces.
                            </p>
                            <div className="space-y-3">
                                <KeywordCard keyword="subject" description="A standalone line with the entity name. The thing you are describing — a service, a person, a component." />
                                <KeywordCard keyword="do:" description="What this entity does. A single action or behavior." />
                                <KeywordCard keyword="needs:" description="What it requires to perform the action. Comma-separated list of inputs." />
                                <KeywordCard keyword="yields:" description="What it produces after performing the action. Comma-separated list of outputs." />
                                <KeywordCard keyword="at:" description="Optional. Where this action happens. A location context for visual grouping." />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Connections — split: code left, explanation right */}
                <section className="py-20 px-6" style={{ borderTop: '1px solid color-mix(in srgb, var(--border) 40%, transparent)' }}>
                    <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-2 lg:gap-12 items-start">
                        <div className="lg:sticky lg:top-24">
                            <CodeBlock filename="connections.en">
                                <div className="text-accent font-medium">auth service</div>
                                <div><span className="text-text-secondary">do: </span>validate token</div>
                                <div><span className="text-text-secondary">needs: </span>jwt token, signing key</div>
                                <div>
                                    <span className="text-text-secondary">yields: </span>
                                    <span className="underline decoration-accent/50 underline-offset-4">auth context</span>
                                </div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">api gateway</div>
                                <div><span className="text-text-secondary">do: </span>route request</div>
                                <div>
                                    <span className="text-text-secondary">needs: </span>
                                    <span className="underline decoration-accent/50 underline-offset-4">auth context</span>
                                    <span>, http request</span>
                                </div>
                                <div><span className="text-text-secondary">yields: </span>routed payload</div>
                            </CodeBlock>
                        </div>
                        <div className="mt-8 lg:mt-0">
                            <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-4">Connections</p>
                            <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
                                Shared names create edges.
                            </h2>
                            <p className="text-text-secondary leading-relaxed mb-6">
                                You never wire entities together explicitly. If one statement yields something and
                                another statement needs the same thing, they are connected automatically.
                                The name is the link.
                            </p>
                            <p className="text-sm text-text-muted leading-relaxed">
                                The auth service yields <code className="font-mono text-accent text-xs px-1.5 py-0.5 rounded" style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}>auth context</code> and
                                the api gateway needs <code className="font-mono text-accent text-xs px-1.5 py-0.5 rounded" style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}>auth context</code> &mdash; so
                                they are connected. No arrows, no config. Just matching names.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Multiple Actions — split: code left, explanation right */}
                <section className="py-20 px-6" style={{ borderTop: '1px solid color-mix(in srgb, var(--border) 40%, transparent)' }}>
                    <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-2 lg:gap-12 items-start">
                        <div className="lg:sticky lg:top-24">
                            <CodeBlock filename="multiple_actions.en">
                                <div className="text-accent font-medium">database</div>
                                <div><span className="text-text-secondary">do: </span>execute read query</div>
                                <div><span className="text-text-secondary">needs: </span>read request, connection pool</div>
                                <div><span className="text-text-secondary">yields: </span>query result</div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">database</div>
                                <div><span className="text-text-secondary">do: </span>execute write query</div>
                                <div><span className="text-text-secondary">needs: </span>write request, connection pool</div>
                                <div><span className="text-text-secondary">yields: </span>write confirmation, audit log</div>
                            </CodeBlock>
                        </div>
                        <div className="mt-8 lg:mt-0">
                            <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-4">Multiple Actions</p>
                            <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
                                One subject, many behaviors.
                            </h2>
                            <p className="text-text-secondary leading-relaxed mb-6">
                                The same subject can appear multiple times, each with a different <code className="font-mono text-text-secondary">do:</code> block.
                                This lets you model entities that perform more than one action.
                            </p>
                            <p className="text-sm text-text-muted leading-relaxed">
                                Both statements belong to the same <code className="font-mono text-accent text-xs px-1.5 py-0.5 rounded" style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}>database</code> entity.
                                The graph engine groups them under one node with two actions.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Shared Actions — split: code left, explanation right */}
                <section className="py-20 px-6" style={{ borderTop: '1px solid color-mix(in srgb, var(--border) 40%, transparent)' }}>
                    <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-2 lg:gap-12 items-start">
                        <div className="lg:sticky lg:top-24">
                            <CodeBlock filename="shared_action.en">
                                <div className="text-accent font-medium">dev, qa, ops</div>
                                <div><span className="text-text-secondary">do: </span>deploy</div>
                                <div><span className="text-text-secondary">needs: </span>artifact, config</div>
                                <div><span className="text-text-secondary">yields: </span>release</div>
                            </CodeBlock>
                        </div>
                        <div className="mt-8 lg:mt-0">
                            <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-4">Shared Actions</p>
                            <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
                                Comma-separated subjects.
                            </h2>
                            <p className="text-text-secondary leading-relaxed mb-6">
                                Multiple entities can share the same action. List them on the subject line
                                separated by commas. This creates one action node with edges from each subject.
                            </p>
                            <p className="text-sm text-text-muted leading-relaxed">
                                All three &mdash; dev, qa, ops &mdash; perform the same deploy action. The graph engine creates
                                one action node with DO edges from each subject.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Location — split: code left, explanation right */}
                <section className="py-20 px-6" style={{ borderTop: '1px solid color-mix(in srgb, var(--border) 40%, transparent)' }}>
                    <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-2 lg:gap-12 items-start">
                        <div className="lg:sticky lg:top-24">
                            <CodeBlock filename="location.en">
                                <div className="text-accent font-medium">payment gateway</div>
                                <div><span className="text-text-secondary">do: </span>process payment</div>
                                <div><span className="text-text-secondary">needs: </span>order total, card token</div>
                                <div><span className="text-text-secondary">yields: </span>payment confirmation</div>
                                <div><span className="text-text-secondary">at: </span><span className="text-text-muted">checkout service</span></div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">fraud engine</div>
                                <div><span className="text-text-secondary">do: </span>score risk</div>
                                <div><span className="text-text-secondary">needs: </span>payment confirmation, customer history</div>
                                <div><span className="text-text-secondary">yields: </span>risk score</div>
                                <div><span className="text-text-secondary">at: </span><span className="text-text-muted">risk platform</span></div>
                            </CodeBlock>
                        </div>
                        <div className="mt-8 lg:mt-0">
                            <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-4">Location</p>
                            <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
                                Place actions with at.
                            </h2>
                            <p className="text-text-secondary leading-relaxed mb-6">
                                Use <code className="font-mono text-text-secondary">at:</code> to say where an action happens.
                                This is optional &mdash; it adds a location context to the graph without changing
                                the dependency structure.
                            </p>
                            <p className="text-sm text-text-muted leading-relaxed">
                                Locations group actions visually in the graph. The keyword order is always:
                                <code className="font-mono text-accent text-xs mx-1 px-1.5 py-0.5 rounded" style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}>do</code>
                                <code className="font-mono text-accent text-xs mx-1 px-1.5 py-0.5 rounded" style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}>needs</code>
                                <code className="font-mono text-accent text-xs mx-1 px-1.5 py-0.5 rounded" style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}>yields</code>
                                <code className="font-mono text-accent text-xs mx-1 px-1.5 py-0.5 rounded" style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}>at</code>.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Comments — split: code left, explanation right */}
                <section className="py-20 px-6" style={{ borderTop: '1px solid color-mix(in srgb, var(--border) 40%, transparent)' }}>
                    <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-2 lg:gap-12 items-start">
                        <div className="lg:sticky lg:top-24">
                            <CodeBlock filename="comments.en">
                                <div className="text-text-muted"># --- User-facing services ---</div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">web app</div>
                                <div><span className="text-text-secondary">do: </span>render dashboard</div>
                                <div><span className="text-text-secondary">needs: </span>user session, api response</div>
                                <div><span className="text-text-secondary">yields: </span>rendered page</div>
                                <div className="h-3" />
                                <div className="text-text-muted"># --- Internal services ---</div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">analytics engine</div>
                                <div><span className="text-text-secondary">do: </span>aggregate metrics</div>
                                <div><span className="text-text-secondary">needs: </span>event stream, time window</div>
                                <div><span className="text-text-secondary">yields: </span>api response</div>
                            </CodeBlock>
                        </div>
                        <div className="mt-8 lg:mt-0">
                            <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-4">Comments</p>
                            <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
                                Annotate with #.
                            </h2>
                            <p className="text-text-secondary leading-relaxed">
                                Lines starting with <code className="font-mono text-text-secondary">#</code> are
                                comments. Use them to organize sections, leave notes, or document intent.
                                The parser ignores them entirely.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Origin — Paninian Karaka — split: explanation left, table right */}
                <section className="py-20 px-6" style={{ borderTop: '1px solid color-mix(in srgb, var(--border) 40%, transparent)' }}>
                    <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-[2fr_3fr] lg:gap-12 items-start">
                        <div className="lg:sticky lg:top-24">
                            <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-4">Origin</p>
                            <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
                                2,500 years of grammar.
                            </h2>
                            <p className="text-text-secondary leading-relaxed mb-6">
                                EN&rsquo;s keywords map directly to P&#257;&#7751;ini&rsquo;s k&#257;raka system &mdash;
                                the semantic roles from ancient Sanskrit grammar (~500 BCE). The same framework that was
                                sufficient for humans to describe any action is sufficient to describe any system.
                            </p>
                            <p className="text-sm text-text-muted leading-relaxed mb-3">
                                P&#257;&#7751;ini defined 6 k&#257;rakas &mdash; semantic roles that fully describe
                                any action in any language. EN maps 5 of them to keywords.
                                <code className="font-mono text-accent text-xs mx-1">needs:</code> merges
                                Kara&#7751;a (instrument) and Ap&#257;d&#257;na (source) because a function&rsquo;s
                                parameters serve as both.
                            </p>
                            <p className="text-sm text-text-muted leading-relaxed">
                                <code className="font-mono text-accent text-xs mr-1">do:</code> maps to Kriy&#257;
                                (&#2325;&#2381;&#2352;&#2367;&#2351;&#2366;) &mdash; the verb itself, not a k&#257;raka
                                but the action all roles revolve around.
                            </p>
                        </div>
                        <div className="mt-8 lg:mt-0">
                            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-surface-elevated" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                            <th className="text-left px-5 py-3 font-mono text-text-muted text-xs uppercase tracking-wider">K&#257;raka</th>
                                            <th className="text-left px-5 py-3 font-mono text-text-muted text-xs uppercase tracking-wider">Role</th>
                                            <th className="text-left px-5 py-3 font-mono text-accent text-xs uppercase tracking-wider">EN</th>
                                            <th className="text-left px-5 py-3 font-mono text-text-muted text-xs uppercase tracking-wider hidden sm:table-cell">Programming</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                            <td className="px-5 py-3 text-text-secondary">Kart&#7771; (&#2325;&#2352;&#2381;&#2340;&#2371;)</td>
                                            <td className="px-5 py-3 text-text-secondary">The doer</td>
                                            <td className="px-5 py-3 font-mono text-accent">subject</td>
                                            <td className="px-5 py-3 text-text-muted hidden sm:table-cell">caller / this</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                            <td className="px-5 py-3 text-text-secondary">Karma (&#2325;&#2352;&#2381;&#2350;)</td>
                                            <td className="px-5 py-3 text-text-secondary">The object / result</td>
                                            <td className="px-5 py-3 font-mono text-accent">yields:</td>
                                            <td className="px-5 py-3 text-text-muted hidden sm:table-cell">return values</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                            <td className="px-5 py-3 text-text-secondary">Kara&#7751;a (&#2325;&#2352;&#2339;)</td>
                                            <td className="px-5 py-3 text-text-secondary">The instrument</td>
                                            <td className="px-5 py-3 font-mono text-accent" rowSpan={2}>needs:</td>
                                            <td className="px-5 py-3 text-text-muted hidden sm:table-cell" rowSpan={2}>parameters</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                            <td className="px-5 py-3 text-text-secondary">Ap&#257;d&#257;na (&#2309;&#2346;&#2366;&#2342;&#2366;&#2344;)</td>
                                            <td className="px-5 py-3 text-text-secondary">The source</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                            <td className="px-5 py-3 text-text-secondary">Adhikara&#7751;a (&#2309;&#2343;&#2367;&#2325;&#2352;&#2339;)</td>
                                            <td className="px-5 py-3 text-text-secondary">The locus</td>
                                            <td className="px-5 py-3 font-mono text-accent">at:</td>
                                            <td className="px-5 py-3 text-text-muted hidden sm:table-cell">namespace / context</td>
                                        </tr>
                                        <tr>
                                            <td className="px-5 py-3 text-text-secondary">Samprad&#257;na (&#2360;&#2350;&#2381;&#2346;&#2381;&#2352;&#2342;&#2366;&#2344;)</td>
                                            <td className="px-5 py-3 text-text-secondary">The recipient</td>
                                            <td className="px-5 py-3 text-text-muted" colSpan={2}>&mdash; implicit in graph flow</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Try It — CTA */}
                <section className="py-20 px-6" style={{ borderTop: '1px solid color-mix(in srgb, var(--border) 40%, transparent)' }}>
                    <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-2 lg:gap-12 items-start">
                        <div className="text-left lg:sticky lg:top-24">
                            <CodeBlock filename="example.en">
                                <div className="text-text-muted"># e-commerce checkout</div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">cart service</div>
                                <div><span className="text-text-secondary">do: </span>calculate totals</div>
                                <div><span className="text-text-secondary">needs: </span>cart items, pricing rules</div>
                                <div><span className="text-text-secondary">yields: </span>order summary, tax amount</div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">payment gateway</div>
                                <div><span className="text-text-secondary">do: </span>process payment</div>
                                <div><span className="text-text-secondary">needs: </span>order summary, payment method</div>
                                <div><span className="text-text-secondary">yields: </span>payment confirmation, receipt</div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">fulfillment</div>
                                <div><span className="text-text-secondary">do: </span>ship order</div>
                                <div><span className="text-text-secondary">needs: </span>payment confirmation, shipping address</div>
                                <div><span className="text-text-secondary">yields: </span>tracking number</div>
                            </CodeBlock>
                        </div>
                        <div className="mt-8 lg:mt-0">
                            <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-4">Try It</p>
                            <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
                                Write your first system.
                            </h2>
                            <p className="text-text-secondary mb-10 leading-relaxed">
                                Open the playground with a pre-filled example. Edit, add entities, and see the
                                graph update in real time.
                            </p>
                            <a
                                href={tryItUrl}
                                className="inline-block bg-accent text-bg font-bold px-8 py-3.5 rounded-full text-sm hover:opacity-90 transition-opacity"
                            >
                                Open in Playground
                            </a>
                        </div>
                    </div>
                </section>
            </main>
            <LandingFooter />
        </div>
    )
}
